import axios from "axios";
import { Capacitor } from "@capacitor/core";
import * as Common from "../utiles/common.util";
import * as store from "../stores/authStore";
import { Preferences } from "@capacitor/preferences";
import { push } from "svelte-spa-router";

const version = "/api/";

const isNative = Capacitor.isNativePlatform(); // 앱이면 true
const DEV_PC_IP = "192.168.1.6";

const host = window.location.hostname;
const isDevHost = host === 'localhost' || host.startsWith('192.168.') || host === '10.0.2.2';
let baseURL = "";

// 1. 모바일 앱 (실제 디바이스 + 에뮬레이터)
if (isNative) {
    if (host.startsWith('localhost') || host === '10.0.2.2') {
        baseURL = `http://${DEV_PC_IP}${version}`;
    } else {
        // 운영 빌드
        baseURL = "https://retechapp.asijang.co.kr" + version;
    }
}
// 2. 웹 브라우저
else {
    baseURL = isDevHost
        ? `http://${window.location.hostname}${version}` // 개발용
        : version; // 운영용 (Nginx /api/ 프록시 경유, Same-Origin)
}

const instance = axios.create({
    baseURL,
    timeout: 54000,
    withCredentials: true,
});

// ---------------------------------------------------------------------
// [접속 DB 선택] R: 운영 DB(ERPG_RETECH), T: 개발 DB(ERPG_RETECH_TEST)
// - 기기(브라우저)마다 Preferences 에 저장, 모든 요청에 X-DB-Target 헤더로 전달
const DB_TARGET_KEY = "retechDbTarget";
let dbTargetCache = null;

export async function getDbTarget() {
    if (dbTargetCache === null) {
        const { value } = await Preferences.get({ key: DB_TARGET_KEY });
        dbTargetCache = value === "T" ? "T" : "R";
    }
    return dbTargetCache;
}

export async function setDbTarget(target) {
    dbTargetCache = target === "T" ? "T" : "R";
    await Preferences.set({ key: DB_TARGET_KEY, value: dbTargetCache });
}

// 운영 DB(기본값)로 복구 (로그아웃 / 강제 로그아웃 시 사용)
export async function resetDbTarget() {
    dbTargetCache = "R";
    await Preferences.remove({ key: DB_TARGET_KEY });
}

// ---------------------------------------------------------------------
// [토큰 갱신 관련 변수]
let isRefreshing = false;
let subscribers = [];

function onRefreshed(newToken) {
    subscribers.forEach((callback) => callback(newToken));
    subscribers = [];
}

function addSubscriber(callback) {
    subscribers.push(callback);
}
// ---------------------------------------------------------------------

// 요청 인터셉터
instance.interceptors.request.use(
    async (config) => {
        const token = await Common.getStoredToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        config.headers["X-DB-Target"] = await getDbTarget();

        // 💡 [공통 파라미터 강제 주입]
        // bplc_code 는 무조건 '01'
        const globalBplc = "01";

        // 1. GET 요청 (params)
        if (config.method === "get") {
            config.params = config.params || {};
            // URL에 이미 포함되어 있는지 확인 (중복 방지: 01,01 현상 해결)
            const hasInUrl = (key) => config.url && config.url.includes(`${key}=`);
            if (!config.params.bplc_code && !hasInUrl("bplc_code")) config.params.bplc_code = globalBplc;
            if (!config.params.as_bplc_code && !hasInUrl("as_bplc_code")) config.params.as_bplc_code = globalBplc;
        }
        // 2. POST, PUT 등 (data)
        else {
            if (config.data instanceof FormData) {
                if (!config.data.has("bplc_code")) config.data.append("bplc_code", globalBplc);
                if (!config.data.has("as_bplc_code")) config.data.append("as_bplc_code", globalBplc);
            } else if (config.data && typeof config.data === "object") {
                if (!config.data.bplc_code) config.data.bplc_code = globalBplc;
                if (!config.data.as_bplc_code) config.data.as_bplc_code = globalBplc;
            } else if (!config.data) {
                // 데이터가 아예 없는 경우 객체로 생성하여 주입
                config.data = {
                    bplc_code: globalBplc,
                    as_bplc_code: globalBplc
                };
            }
        }

        return config;
    },
    (error) => Promise.reject(error),
);

// 응답 인터셉터 (401 에러 감지 시 Refresh 로직 실행)
instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config, response } = error;
        const originalRequest = config;

        // 401 에러(만료)가 났고, 재시도한 적이 없는 요청일 때
        if (response && response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // 이미 다른 요청이 갱신 중이라면 큐에서 대기
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    addSubscriber((newToken) => {
                        if (newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                            resolve(instance(originalRequest));
                        } else {
                            reject(error);
                        }
                    });
                });
            }

            isRefreshing = true;

            try {
                // 리프레시 토큰 가져오기 (Common 유틸에 getStoredRefreshToken 함수가 있다고 가정)
                const refreshToken = await Common.getStoredRefreshToken();

                if (!refreshToken) {
                    throw new Error("No refresh token");
                }

                // 서버에 토큰 갱신 요청 (FormData 형식 유지)
                const formData = new FormData();
                formData.append("refresh_token", refreshToken);

                const refreshRes = await axios.post(
                    baseURL + "account/refresh",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                            "X-DB-Target": await getDbTarget(),
                        },
                    },
                );

                if (refreshRes.data.code === 200) {
                    const newAccessToken = refreshRes.data.token.jwt_token;
                    const newRefreshToken = refreshRes.data.token.refresh_token;

                    store.retech_jwt_token.set(newAccessToken);
                    store.retech_refresh_token.set(newRefreshToken);

                    // 새로운 토큰들 저장
                    await Preferences.set({
                        key: "retech_jwt_token",
                        value: newAccessToken,
                    });
                    if (newRefreshToken) {
                        await Preferences.set({
                            key: "retech_refresh_token",
                            value: newRefreshToken,
                        });
                    }

                    isRefreshing = false;
                    onRefreshed(newAccessToken);

                    // 원래 실패했던 요청 재시도
                    originalRequest.headers = {
                        ...originalRequest.headers,
                        Authorization: `Bearer ${newAccessToken}`,
                    };

                    return instance(originalRequest);
                } else {
                    throw new Error("Refresh failed with code " + refreshRes.data.code);
                }
            } catch (refreshError) {
                isRefreshing = false;
                // 에러 발생 시 대기 중인 모든 요청들을 reject 처리
                subscribers.forEach((callback) => callback(null));
                subscribers = [];

                await Preferences.remove({ key: "retechInfo" });
                await Preferences.remove({ key: "retech_jwt_token" });
                await Preferences.remove({ key: "retech_refresh_token" });
                // 인증 실패 시 운영 DB 로 되돌림 (개발 DB 에 계정이 없는 경우 로그인 불가 방지)
                await resetDbTarget();

                // 로그인 페이지로 이동
                push("/Login");
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    },
);

export default instance;
